import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput from "../../Main/Record/PhoneInput/PhoneInput";

export default function RecordPopup({
  doctor,
  schedule,
  show,
  onClose,
  defaultType,
  showTypeSelector,
}) {
  const recaptchaRef = useRef(null);

  const [isAgree, setIsAgree] = useState(false);
  const [isOmsAgree, setIsOmsAgree] = useState(false);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupStatus, setPopupStatus] = useState("sending");

  const [captchaVerified, setCaptchaVerified] = useState(false);
  // sending | success | error

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://api.urniidvi.ru"
      : "http://localhost:5000";

  const [form, setForm] = useState({
    fio: "",
    phone: "+7",
    date: "",
    type: defaultType || "paid",
  });

  useEffect(() => {
    if (show) {
      setStep(1);
      setCaptchaVerified(false);
      setIsAgree(false);
      setIsOmsAgree(false);

      setForm({
        fio: "",
        phone: "+7",
        date: "",
        type: defaultType || "paid",
      });

      recaptchaRef.current?.reset();
    }
  }, [show, defaultType]);

  if (!show || !doctor || !schedule) return null;

  const workDays = schedule.work
    .map((day, index) => {
      if (day && day !== "-" && day.trim() !== "") return index + 1;
      return null;
    })
    .filter(Boolean);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 15; i++) {
      const future = new Date();
      future.setDate(today.getDate() + i);

      const jsDay = future.getDay();
      const normalize = jsDay === 0 ? 7 : jsDay;

      if (workDays.includes(normalize)) {
        dates.push(new Date(future));
      }
    }

    return dates;
  };

  const availableDates = generateAvailableDates();

  const formatDate = (date) => {
    return date.toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      weekday: "short",
    });
  };

  const toInputFormat = (date) => {
    return date.toISOString().split("T")[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "type") {
      setIsOmsAgree(false);
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const phoneDigits = form.phone.replace(/\D/g, "");

  const isFioValid = form.fio.trim().length >= 2;
  const isPhoneValid = phoneDigits.length === 11;
  const isStep2Valid = isFioValid && isPhoneValid;

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupStatus("sending");
  };

  const submit = async () => {
    const recaptchaValue = recaptchaRef.current?.getValue();

    if (!isAgree || !captchaVerified || (form.type === "oms" && !isOmsAgree)) {
      return;
    }

    if (!recaptchaValue) {
      setPopupStatus("error");
      setShowPopup(true);
      return;
    }

    setLoading(true);
    setPopupStatus("sending");
    setShowPopup(true);

    try {
      const res = await fetch(`${API_URL}/api/mail-doctor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fio: form.fio,
          phone: form.phone,
          date: form.date,
          type: form.type === "oms" ? "Прием по ОМС" : "Платный прием",
          doctor: doctor.name,
          cabinet: schedule.cabinet,
          to_email: "urniidvi-online@mail.ru",
        }),
      });

      if (!res.ok) throw new Error();

      setPopupStatus("success");

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("MAIL ERROR:", err);
      setPopupStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="popup__overlay" onClick={onClose}>
      <div
        className="popup__content recordpopup"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="popup__close" onClick={onClose}>
          ✕
        </button>

        <div className="recordpopup__body">
          <h2 className="recordpopup__title">Запись на прием</h2>

          <div className="recordpopup__doctor">
            <p className="recordpopup__doctor-text">Врач: {doctor.name}</p>
            <p>Кабинет: {schedule.cabinet}</p>
          </div>

          {step === 1 && (
            <div className="recordpopup__step">
              <h3 className="recordpopup__doctor-text">Выберите дату приема</h3>

              <div className="recordpopup__dates_wrap">
                <div className="recordpopup__dates">
                  {availableDates.map((dateItem, index) => {
                    const value = toInputFormat(dateItem);

                    return (
                      <button
                        key={index}
                        type="button"
                        className={`recordpopup__datebtn ${
                          form.date === value ? "active" : ""
                        }`}
                        onClick={() => setForm((p) => ({ ...p, date: value }))}
                      >
                        {formatDate(dateItem)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="popup__actions">
                <button
                  onClick={next}
                  disabled={!form.date}
                  style={{
                    background: form.date ? "#2b7cff" : "#e0e0e0",
                    color: form.date ? "#fff" : "#999",
                    cursor: form.date ? "pointer" : "not-allowed",
                  }}
                >
                  Далее
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="recordpopup__step">
              <h3 className="recordpopup__doctor-text">Введите ваши данные</h3>

              <input
                type="text"
                name="fio"
                placeholder="ФИО"
                value={form.fio}
                onChange={handleChange}
                className={`record__input ${!isFioValid && form.fio ? "error" : ""}`}
              />

              <PhoneInput
                className={`record__input ${
                  !isPhoneValid && form.phone !== "+7" ? "error" : ""
                }`}
                name="phone"
                placeholder="+7(___)___-__-__"
                value={form.phone}
                onChange={handleChange}
              />

              {showTypeSelector && (
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="record__input"
                >
                  <option value="paid">Платный</option>
                  <option value="oms">ОМС</option>
                </select>
              )}
              {form.type === "oms" && (
                <div className="recordpopup__oms-warning" role="alert">
                  <strong>Внимание!</strong>
                  <p>
                    Прием осуществляется при наличии направления формы № 057/у
                    из государственной поликлиники по месту прикрепления.
                  </p>
                </div>
              )}

              <div className="popup__actions">
                <button onClick={back}>Назад</button>
                <button
                  onClick={next}
                  disabled={!isStep2Valid}
                  style={{
                    background: isStep2Valid ? "#2b7cff" : "#e0e0e0",
                    color: isStep2Valid ? "#fff" : "#999",
                    cursor: isStep2Valid ? "pointer" : "not-allowed",
                  }}
                >
                  Далее
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="recordpopup__step">
              <h3 className="recordpopup__doctor-text">Подтверждение записи</h3>

              <p className="recordpopup__doctor-text">Дата: {form.date}</p>
              <p className="recordpopup__doctor-text">Пациент: {form.fio}</p>
              <p className="recordpopup__doctor-text">Телефон: {form.phone}</p>

              <ReCAPTCHA
                sitekey="6LdGG7srAAAAAGK6wMRUdW1xdkSsS6areWMsJhsW"
                ref={recaptchaRef}
                onChange={(token) => setCaptchaVerified(!!token)}
                onExpired={() => setCaptchaVerified(false)}
              />
              <label className="record__agree recordpopup__agree">
                {" "}
                <input
                  type="checkbox"
                  checked={isAgree}
                  onChange={() => setIsAgree(!isAgree)}
                />
                <div>
                  {" "}
                  Я даю согласие на обработку персональных данных ({" "}
                  <a
                    href="/about/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="question-answer-text"
                  >
                    {" "}
                    политика{" "}
                  </a>{" "}
                  ){" "}
                </div>
              </label>

              {defaultType === "oms" && (
                <label className="record__agree recordpopup__agree">
                  <input
                    type="checkbox"
                    checked={isOmsAgree}
                    onChange={(e) => setIsOmsAgree(e.target.checked)}
                  />{" "}
                  Я уведомлен, что без направления формы 057/у прием по ОМС
                  невозможен, и согласен с условиями.
                </label>
              )}

              <div className="popup__actions">
                <button onClick={back}>Назад</button>
                <button
                  onClick={submit}
                  disabled={
                    loading ||
                    !isAgree ||
                    !captchaVerified ||
                    (form.type === "oms" && !isOmsAgree)
                  }
                  style={{
                    background:
                      loading ||
                      !isAgree ||
                      !captchaVerified ||
                      (form.type === "oms" && !isOmsAgree)
                        ? "#e0e0e0"
                        : "#2b7cff",
                    color:
                      loading ||
                      !isAgree ||
                      !captchaVerified ||
                      (form.type === "oms" && !isOmsAgree)
                        ? "#999"
                        : "#fff",
                    cursor:
                      loading ||
                      !isAgree ||
                      !captchaVerified ||
                      (form.type === "oms" && !isOmsAgree)
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {loading ? "Отправка..." : "Подтвердить"}
                </button>
              </div>
            </div>
          )}
        </div>
        {showPopup && (
          <div className="popup__overlay" onClick={handleClosePopup}>
            <div
              className="popup__content"
              onClick={(e) => e.stopPropagation()}
            >
              {popupStatus === "sending" && (
                <>
                  <h3 className="popup__title">Отправка...</h3>
                  <p className="popup__text">Пожалуйста, подождите.</p>
                </>
              )}

              {popupStatus === "success" && (
                <>
                  <h3 className="popup__title">Заявка отправлена!</h3>
                  <p className="popup__text">
                    Администратор скоро свяжется с вами.
                  </p>
                </>
              )}

              {popupStatus === "error" && (
                <>
                  <h3 className="popup__title">Ошибка</h3>
                  <p className="popup__text">
                    Не удалось отправить заявку. Подтвердите, что вы не робот
                  </p>
                </>
              )}

              <button className="record__button" onClick={handleClosePopup}>
                Закрыть
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
